import React, { useEffect, useRef, useState } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ProjectImageUpload = ({
  value,
  onChange,
  disabled = false,
  size = 270,
  width,
  height,
}) => {
  const resolvedWidth = width !== undefined ? width : size;
  const resolvedHeight = height !== undefined ? height : size;
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!value) {
      setPreviewUrl("");
      return;
    }
    if (typeof value === "string") {
      setPreviewUrl(value);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) onChange?.(file);
    e.target.value = "";
  };

  const triggerFileInput = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const handleBoxClick = () => {
    if (disabled) return;
    if (previewUrl) {
      setLightboxOpen(true);
    } else {
      triggerFileInput();
    }
  };

  return (
    <Box>
      <Box
        onClick={handleBoxClick}
        sx={{
          width: resolvedWidth,
          height: resolvedHeight,
          borderRadius: "12px",
          border: previewUrl ? "1px solid #E5E7EB" : "1px dashed #9CA3AF",
          backgroundColor: previewUrl ? "#fff" : "#F9FAFB",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
          cursor: disabled
            ? "not-allowed"
            : previewUrl
            ? "zoom-in"
            : "pointer",
          transition: "border-color 0.2s ease",
          "&:hover": {
            borderColor: disabled
              ? undefined
              : previewUrl
              ? "#E5E7EB"
              : "#4D6BF6",
          },
        }}
      >
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt="Project"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {!disabled && (
              <IconButton
                size="small"
                onClick={handleRemove}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255,255,255,0.92)",
                  "&:hover": { backgroundColor: "#fff" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18, color: "#EF4444" }} />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#fff",
                border: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0D0D0E",
                mb: 1.25,
              }}
            >
              <AddIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
              }}
            >
              Upload Image
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "#6B7280",
                mt: 0.25,
                fontFamily: "Inter Tight",
              }}
            >
              PNG or JPG
            </Typography>
          </>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleSelect}
      />

      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: "absolute",
              top: -36,
              right: -8,
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.5)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Project"
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                borderRadius: 8,
                display: "block",
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ProjectImageUpload;
